import { useState } from 'react'

import { Link as RouterLink } from 'react-router-dom'
import FileFillIcon from 'remixicon-react/FileFillIcon'

import {
  SpaceListOrderByEnum,
  Space,
  SpaceType,
} from '@tribeplatform/gql-client/types'
import {
  useSpaces,
  useNetwork,
  SystemSpaceSlug,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'
import { SearchIcon } from '@tribeplatform/react-ui-kit/icons'
import { Input } from '@tribeplatform/react-ui-kit/Input'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { NavVertical } from '@tribeplatform/react-ui-kit/Sidebar'

import { useDebounce } from '../../../utils/useDebounce'
import { SystemSpacesIcons } from '../../MainMenu/constants'
import { SpaceImage } from '../../Space/SpaceImage'

export const ManageSpacesListSidebar = () => {
  const [query, setQuery] = useState('')
  const { data: network } = useNetwork()
  const [showMoreSystemSpaces, setShowMoreSystemSpaces] = useState(false)
  const systemSpaces = network.systemSpaces.filter(
    s => s.type !== SpaceType.ENTITY && s.slug !== SystemSpaceSlug.HOME,
  )
  const debouncedQuery = useDebounce(query, 500)
  const { data: paginatedSpaces } = useSpaces({
    fields: {
      image: 'basic',
      collection: 'basic',
    },
    variables: {
      limit: 500,
      query: debouncedQuery,
      type: [SpaceType.GROUP],
      orderBy: SpaceListOrderByEnum.CUSTOM_ORDERING_INDEX,
    },
  })
  const homeSpace = network.systemSpaces.find(
    s => s.slug === SystemSpaceSlug.HOME,
  )

  const { nodes: customSpaces } =
    simplifyPaginatedResult<Space>(paginatedSpaces)

  const spaceByCollections = customSpaces?.reduce<{ [key: string]: Space[] }>(
    (collections, space) => {
      if (!Array.isArray(collections[space.collection?.id])) {
        collections[space.collection?.id] = []
      }
      collections[space.collection?.id].push(space)
      return collections
    },
    {},
  )

  const HomeIcon = SystemSpacesIcons[SystemSpaceSlug.HOME]
  return (
    <>
      <div>
        <Input
          leadingIcon={<SearchIcon />}
          placeholder="Search"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <NavVertical>
          <NavVertical.Group className="mt-3">
            <div className="space-y-1 flex flex-col">
              <NavVertical.Item
                as={RouterLink}
                to={`/manage/editor/spaces/${homeSpace.id}/blocks`}
                key={homeSpace.id}
                leadingIcon={<HomeIcon />}
              >
                <div className="truncate">{homeSpace.name}</div>
              </NavVertical.Item>
              {systemSpaces
                ?.slice(
                  0,
                  showMoreSystemSpaces
                    ? Infinity
                    : (spaceByCollections?.undefined?.length || 0) + 2,
                )
                ?.map(space => {
                  const Icon = SystemSpacesIcons[space.slug] || FileFillIcon
                  return (
                    <NavVertical.Item
                      as={RouterLink}
                      to={`/manage/editor/spaces/${space.id}/blocks`}
                      key={space.id}
                      leadingIcon={<Icon />}
                    >
                      <div className="truncate">{space.name}</div>
                    </NavVertical.Item>
                  )
                })}
            </div>
            <div>
              <Link
                className="block mt-3 text-sm text-center text-basicSurface-500 hover:text-basicSurface-300"
                onClick={() => setShowMoreSystemSpaces(!showMoreSystemSpaces)}
              >
                {showMoreSystemSpaces ? 'Show less' : 'Show more'}
              </Link>
            </div>
          </NavVertical.Group>
          {spaceByCollections &&
            Object.entries(spaceByCollections).map(([key, value]) => (
              <NavVertical.Group key={key}>
                <h6 className="text-basicSurface-500">
                  {value[0]?.collection?.name || ''}
                </h6>
                <div className="space-y-1 flex flex-col">
                  {value?.map(space => (
                    <NavVertical.Item
                      as={RouterLink}
                      to={`/manage/editor/spaces/${space.id}/blocks`}
                      key={space.id}
                    >
                      <div className="mr-3 -ml-1">
                        <SpaceImage space={space} size="sm" />
                      </div>
                      <div className="truncate">{space.name}</div>
                    </NavVertical.Item>
                  ))}
                </div>
              </NavVertical.Group>
            ))}
        </NavVertical>
      </div>
    </>
  )
}
